/// FOURJS_START_COPYRIGHT(D,2025)
/// Property of Four Js*
/// (c) Copyright Four Js 2025, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
"use strict";

modulum("TagEditWidget", ["EditWidget", "WidgetFactory"], function (context, cls) {
  /**
   * @class TagEditWidget
   * @memberOf classes
   * @extends classes.EditWidget
   * @publicdoc Widgets
   */
  cls.TagEditWidget = context.oo.Class(cls.EditWidget, function ($super) {
    /** @lends classes.TagEditWidget.prototype */
    return {
      __name: "TagEditWidget",
      __templateName: "TagEditWidget",

      _segmentSeparator: ";",
      _forcedCompleter: false,
      _onCompleterDataChanged: null,

      /**
       * @param {*} opts - Options passed to the constructor
       */
      constructor: function (opts) {
        $super.constructor.call(this, opts);
        this._tags = [];
        this._focusedTagIndex = -1;
        this._allowTagCreation = true;
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._tagContainer = this.getElement().querySelector(`.${this.getBEMPartName("container")}`);
        this._removeAllTag = this.getElement().querySelector(`.${this.getBEMPartName("removeAll")}`);
      },

      /**
       * The "remove all" button follows the same enabled regime as the input.
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        const elements = $super.getDOMTabIndexElements.call(this);
        if (this._removeAllTag) {
          elements.push(this._removeAllTag);
        }
        return elements;
      },

      /**
       * Will add a completer to the edit
       * @inheritDoc
       */
      addCompleterWidget: function () {
        if (this.hasCompleter()) {
          return;
        }
        $super.addCompleterWidget.call(this);
        // Configure completer to only display the last segment after the separator
        this.getCompleterWidget().configureSegmentDisplay(true, this._segmentSeparator);
        this.getCompleterWidget().setAllowCompleterReopen(true);
        this._onCompleterDataChanged = this.getCompleterWidget().when(
          context.constants.widgetEvents.completerDataChanged,
          this._addCustomChoice.bind(this)
        );
      },

      /**
       * Add or update a custom completer choice based on the current input
       * - If the input is empty or ends with the separator, no choice is added
       * - Otherwise, appends the pending value as a "new tag" suggestion
       * @private
       */
      _addCustomChoice: function () {
        const inputValue = this.getInputElement().value;
        const completer = this.getCompleterWidget();

        // Guard 1: nothing to add if input is empty or already ends with the segment separator
        if (!inputValue || inputValue.endsWith(this._segmentSeparator) || !this._allowTagCreation) {
          if (this._forcedCompleter) {
            completer.hideDropDown();
          }
          return;
        }
        // Guard 2: if tag creation is not allowed, stop here
        if (!this._allowTagCreation) {
          if (this._forcedCompleter) {
            completer.hideDropDown();
          }
          return;
        }
        // Guard 3: prevent suggesting a new label if the current input already matches an existing completer choice (avoid duplicates)
        if (completer.getChoices().some((choice) => choice.toLowerCase() === (this.getValue() + this._segmentSeparator).toLowerCase())) {
          if (this._forcedCompleter) {
            completer.hideDropDown();
          }
          return;
        }
        // Guard 4: prevent suggesting a tag if it already exists in the tag list
        if (this._tags.some((tag) => tag.toLowerCase() === inputValue.toLowerCase())) {
          if (this._forcedCompleter) {
            completer.hideDropDown();
          }
          return;
        }

        // Success: add a "new label" suggestion and display the dropdown
        completer.addChoice(this.getValue() + " " + gbc.I18NService.t("gwc.main.tagedit.newTag") + this._segmentSeparator);
        completer.showDropDown();
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onRequestFocus(domEvent);
        if (this.isEnabled()) {
          if (domEvent.target.closest(`.${this.getBEMPartName("tagRemove")}`)) {
            const tagEl = domEvent.target.closest(`.${this.getBEMPartName("tag")}`);
            if (tagEl) {
              // Find the tag index in the container
              const index = Array.prototype.indexOf.call(this._tagContainer.children, tagEl);
              this._removeTag(index);
            }
          }

          // Remove all tags
          if (domEvent.target.closest(`.${this.getBEMPartName("removeAll")}`)) {
            this._removeAllTags();
          }

          if (this.getInputElement().contains(domEvent.target)) {
            this._clearTagFocus();
          }
        }

        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        switch (keyString) {
          case "backspace":
            if (this._removeLastTag()) {
              keyProcessed = true;
            }
            break;
          case "del":
          case "delete":
            this._removeTag(this._focusedTagIndex);
            keyProcessed = true;
            break;
          case "left":
            if (this._moveTagFocusLeft()) {
              keyProcessed = true;
            }
            break;
          case "right":
            if (this._moveTagFocusRight()) {
              keyProcessed = true;
            }
            break;
          default:
            this._clearTagFocus();
            break;
        }

        if (keyProcessed) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      manageInput: function (dataString = "", event = null) {
        // If no completer is defined, create one manually so we can still propose the current input as a tag
        if (!this.hasCompleter()) {
          this.addCompleterWidget();
          this._forcedCompleter = true;
        }

        // If using a forced completer, refresh its choices on every keystroke
        if (this._forcedCompleter) {
          this.getCompleterWidget().clearChoices();
          this._addCustomChoice();
        }

        $super.manageInput.call(this, dataString, event);
      },

      /**
       * @inheritDoc
       */
      triggerValueChangedEvent: function (newValue, sendValue = true) {
        // When using a forced completer, block automatic VM updates (we only send a value when the user explicitly selects a choice)
        if (this._forcedCompleter) {
          newValue = this.getValue();
          sendValue = false;
          this.setValue(newValue, false);
        }

        if (sendValue === false || newValue?.toString() !== this._oldValue) {
          this.emit(context.constants.widgetEvents.valueChanged, newValue, sendValue);
        }
      },

      /**
       * @returns {string} the displayed value
       * @inheritDoc
       */
      getValue: function () {
        const result = this.getInputElement().value;
        // If there are tags, return them joined by ";" followed by the pending input
        if (this._tags.length > 0) {
          return this._tags.join(this._segmentSeparator) + this._segmentSeparator + result;
        }
        return result;
      },

      /**
       * Value received from VM
       * @param value
       * @param force
       * @inheritDoc
       */
      setValue: function (value, fromVM) {
        let fullValue = "";
        if (value !== null) {
          fullValue = String(value);
        }
        const segments = fullValue.split(this._segmentSeparator);
        const hasTrailingSeparator = fullValue.endsWith(this._segmentSeparator);

        // Compute confirmed tags and pending input explicitly
        let complete = [];
        let pending = "";
        if (hasTrailingSeparator) {
          // All segments are complete tags, nothing pending
          complete = segments.filter(Boolean);
        } else {
          // Last segment still being typed => exclude it from tags
          complete = segments.slice(0, -1).filter(Boolean);
          pending = segments.at(-1) || "";
        }

        this._tags = complete.slice();
        this._renderTags(this._tags);

        // Only update input if trimmed values differ (don’t wipe user spaces)
        if (this.getInputElement().value.trim() !== pending.trim()) {
          $super.setValue.call(this, pending, fromVM);
        }
      },

      /**
       * Renders the tag list as individual chip elements
       * @param {string[]} tags - Tags to render
       * @private
       */
      _renderTags: function (tags) {
        if (!this._tagContainer) {
          return;
        }

        this._tagContainer.innerHTML = "";
        for (const tag of tags) {
          const tagElement = document.createElement("span");
          tagElement.className = this.getBEMPartName("tag");
          tagElement.textContent = tag;

          // Close icon for removing the tag. Kept out of the Tab order: per-tag
          // delete buttons would multiply Tab stops linearly with the tag count
          // (a11y antipattern). Removal happens via mouse click or via the
          // dedicated tagRemove keyboard pattern (Backspace on empty input,
          // arrow-key roving inside the chip strip).
          const closeIcon = document.createElement("button");
          closeIcon.setAttribute("type", "button");
          closeIcon.setAttribute("aria-label", "Close");
          closeIcon.setAttribute("tabindex", "-1");
          closeIcon.className = this.getBEMPartName("tagRemove");
          // Decorative glyph in its own <i> so the button keeps a clean accessible name (aria-label="Close")
          const closeGlyph = document.createElement("i");
          closeGlyph.setAttribute("aria-hidden", "true");
          closeGlyph.className = "zmdi zmdi-close";
          closeIcon.appendChild(closeGlyph);
          tagElement.appendChild(closeIcon);

          this._tagContainer.appendChild(tagElement);

          if (tags.length > 0) {
            this._scrollTagIntoView(tags.length - 1);
          }
        }
        this._updateRemoveAllVisibility();
      },

      /**
       * Removes a tag at the given index and emits a value change to the VM
       * @param {number} index - Index of the tag to remove
       * @private
       */
      _removeTag: function (index) {
        this._tags.splice(index, 1);
        const pending = this.getInputElement().value || "";

        let base = "";
        if (this._tags.length > 0) {
          base = this._tags.join(this._segmentSeparator) + this._segmentSeparator;
        }
        const newValue = base + pending;
        this._updateRemoveAllVisibility();

        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * Remove the last tag if cursor is at the start of input and no tag is focused
       * @private
       * @returns {boolean} True if a tag was removed, false otherwise
       */
      _removeLastTag: function () {
        const items = this._tagContainer?.children || [];
        if (!items.length || this._focusedTagIndex !== -1 || this.getCursors().start !== 0 || this.getCursors().end !== 0) {
          return false;
        }

        this._removeTag(items.length - 1);

        return true;
      },

      /**
       * Removes all tags and emits a value change to the VM
       * @private
       */
      _removeAllTags: function () {
        this._tags = [];
        const newValue = this.getInputElement().value || "";
        this._updateRemoveAllVisibility();
        this._tagContainer.innerHTML = "";
        this.emit(context.constants.widgetEvents.valueChanged, newValue);
      },

      /**
       * Move focus to the previous tag when navigating left
       * @private
       * @returns {boolean} True if tag focus changed, false otherwise
       */
      _moveTagFocusLeft: function () {
        const items = this._tagContainer?.children || [];
        if (!items.length || (this._focusedTagIndex === -1 && this.getCursors().start !== 0)) {
          return false;
        }

        if (this._focusedTagIndex === -1) {
          this._focusedTagIndex = items.length - 1;
        } else {
          this._focusedTagIndex = Math.max(0, this._focusedTagIndex - 1);
        }
        this._focusTagAt(this._focusedTagIndex);

        return true;
      },

      /**
       * Move focus to the next tag when navigating right
       * @private
       * @returns {boolean} True if tag focus changed or clearded, false otherwise
       */
      _moveTagFocusRight: function () {
        const items = this._tagContainer?.children || [];
        if (!items.length || this._focusedTagIndex === -1) {
          return false;
        }

        const lastIdx = items.length - 1;

        if (this._focusedTagIndex === lastIdx) {
          this._clearTagFocus();
        } else {
          this._focusedTagIndex++;
          this._focusTagAt(this._focusedTagIndex);
        }

        return true;
      },

      /**
       * Apply focus style to the tag at the given index and scroll it into view
       * @private
       * @param {number} index - Index of the tag to focus (-1 to clear focus)
       */
      _focusTagAt: function (index) {
        const items = this._tagContainer?.children || [];
        Array.from(items).forEach((el) => el.classList.remove(gbc.constants.widgetCssClasses.isFocused));
        if (index >= 0 && items[index]) {
          items[index].classList.add(gbc.constants.widgetCssClasses.isFocused);
          this._scrollTagIntoView(index);
        }
      },

      /**
       * Remove focus from all tags and reset the focused tag index
       * @private
       */
      _clearTagFocus: function () {
        this._focusTagAt(-1);
        this._focusedTagIndex = -1;
      },

      /**
       * Ensure the focused tag is horizontally visible inside the container
       * @param {number} index - Tag index to reveal
       */
      _scrollTagIntoView: function (index) {
        if (!this._tagContainer.children?.[index]) {
          return;
        }
        const tagEl = this._tagContainer.children[index];

        const padding = 8; // visual padding
        const tagStart = tagEl.offsetLeft;
        const tagEnd = tagStart + tagEl.offsetWidth;

        const viewportStart = this._tagContainer.scrollLeft;
        const viewportEnd = viewportStart + this._tagContainer.clientWidth;

        let targetScroll = null;

        // Scroll left if tag starts before viewport (with padding)
        if (tagStart - padding < viewportStart) {
          targetScroll = Math.max(0, tagStart - padding);
        }
        // Scroll right if tag ends after viewport (with padding)
        else if (tagEnd + padding > viewportEnd) {
          targetScroll = Math.min(tagEnd - this._tagContainer.clientWidth + padding, this._tagContainer.scrollWidth - this._tagContainer.clientWidth);
        }

        if (targetScroll !== null) {
          this._tagContainer.scrollTo({ left: targetScroll, behavior: "smooth" });
        }
      },

      /**
       * Show "remove all" only if there's at least one tag
       * @private
       */
      _updateRemoveAllVisibility: function () {
        if (!this._removeAllTag) {
          return;
        }
        const hasTags = this._tags && this._tags.length > 0;
        cls.DOMElementHelper.toggleClass(this._removeAllTag, "hidden", !hasTags);
      },

      /**
       * Control whether the current input is proposed as a new tag in the completer
       * If false, the "(+)" option will not be shown in the dropdown
       * @param {boolean} value
       */
      setAllowTagCreation: function (value) {
        this._allowTagCreation = value;
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._onCompleterDataChanged) {
          this._onCompleterDataChanged();
          this._onCompleterDataChanged = null;
        }
        this._tags = null;
        this._focusedTagIndex = -1;
        this._tagContainer = null;
        this._removeAllTag = null;

        $super.destroy.call(this);
      }
    };
  });
  // Register the widget with WidgetFactory
  cls.WidgetFactory.registerBuilder("TagEdit", cls.TagEditWidget);
  cls.WidgetFactory.registerBuilder("Edit[customWidget=tagEdit]", cls.TagEditWidget);
  cls.WidgetFactory.registerBuilder("Label[customWidget=tagEdit]", cls.TagEditWidget);
});
